import React, { FC, useMemo, useContext } from 'react';
import { LoadingContext, ILoadingContextValue } from './loading-context';
import { DirectLineContext } from '../direct-line-provider/direct-line-context';
import { useLoading } from '../../hooks/useLoading';
import { IAdsCopilotProps } from '../../types';

interface ILoadingProviderProps {
  children: React.ReactNode;
  onReceiveActivity?: IAdsCopilotProps['onReceiveActivity'];
  renderBottomComponent?: IAdsCopilotProps['renderBottomComponent'];
  isCustomStream?: boolean;
  isStopGeneratingEnabled?: IAdsCopilotProps['isStopGeneratingEnabled'];
  isStopTroubleshootingEnabled?: IAdsCopilotProps['isStopTroubleshootingEnabled'];
  isRefreshUIEnabled?: boolean;
  isProgressIndicatorEnabled?: boolean;
  isLiveAgentInProgress?: boolean;
  onEndLiveAgentChatClick?: () => void;
}

export const LoadingProvider: FC<ILoadingProviderProps> = ({
  children,
  onReceiveActivity,
  renderBottomComponent,
  isCustomStream,
  isStopGeneratingEnabled,
  isStopTroubleshootingEnabled,
  isRefreshUIEnabled,
  isProgressIndicatorEnabled,
  isLiveAgentInProgress,
  onEndLiveAgentChatClick,
}) => {
  const directLineContext = useContext(DirectLineContext);

  // Only initialize useLoading when DirectLineContext is available
  const loadingHook = useLoading({
    onReceiveActivity,
    renderBottomComponent,
    isCustomStream,
    isStopGeneratingEnabled,
    isStopTroubleshootingEnabled,
    isRefreshUIEnabled,
    isProgressIndicatorEnabled,
  });

  const contextValue = useMemo<ILoadingContextValue>(() => ({
    onReceiveActivity: loadingHook.onReceiveActivity,
    renderBottomComponent: loadingHook.renderBottomComponent,
    isGeneratingAnswer: loadingHook.isGeneratingAnswer,
    onClickStopGenerating: loadingHook.onClickStopGenerating,
    isTroubleshooting: loadingHook.isTroubleshooting,
    onClickStopTroubleshooting: loadingHook.onClickStopTroubleshooting,
    isLiveAgentInProgress,
    onEndLiveAgentChatClick,
  }), [loadingHook.onReceiveActivity,
    loadingHook.renderBottomComponent,
    loadingHook.isGeneratingAnswer,
    loadingHook.onClickStopGenerating,
    loadingHook.isTroubleshooting,
    loadingHook.onClickStopTroubleshooting,
    isLiveAgentInProgress,
    onEndLiveAgentChatClick,
  ]);

  // Only provide the context when DirectLine is available
  if (!directLineContext) {
    return <>{children}</>;
  }

  return (
    <LoadingContext.Provider value={contextValue}>
      {children}
    </LoadingContext.Provider>
  );
};
